//
// # Annotations
//

// Local Imports
import { model, schdata, graphics, randomId } from "GenAlphaModel";
import type { RedrawRequest } from "DrawCore/src/redraw";
import type { InteractionHost } from "../../interaction_host";
import * as style from "DrawCore/src/style";
import { Keys } from "../../keys";
import * as modes from "../../modes";
import * as changes from "DrawCore/src/changes";
import { EntityKind } from "DrawCore/src/entity";
import {
  RectEntity,
  PolygonEntity,
  PathEntity,
  EllipseEntity,
  ArcEntity,
  TextEntity,
  Primitive,
} from "DrawCore/src/primitives";
import { GraphicsEntity } from "DrawCore/src/content/shared/graphics";
import type { NonGraphicsContent } from "../content";
import { DrawTo } from "DrawCore/src/draw";
import {
  Annotation,
  AnnotationsRender,
  DEFAULT_STYLE_TABLE,
} from "DrawCore/src/content/shared/annotations_render";

// modes) read it unchanged.
export class Annotations {
  readonly editor: InteractionHost;
  readonly render: AnnotationsRender;

  constructor(
    readonly content: NonGraphicsContent,
    table: style.StyleTable = DEFAULT_STYLE_TABLE,
    // A migrated content type passes its render tier's AnnotationsRender (so its
    // XRender.redraw draws the same graphics). An unmigrated one omits it and we
    // build one parented under `content.render` (which is `content` itself there).
    render?: AnnotationsRender
  ) {
    this.editor = content.editor;
    this.render = render ?? new AnnotationsRender(content.render, table);
  }

  // --- Forwarders to the render tier ---
  get graphicsEntity() {
    return this.render.graphicsEntity;
  }
  get rects() {
    return this.render.rects;
  }
  get polygons() {
    return this.render.polygons;
  }
  get paths() {
    return this.render.paths;
  }
  get ellipses() {
    return this.render.ellipses;
  }
  get arcs() {
    return this.render.arcs;
  }
  get texts() {
    return this.render.texts;
  }
  registerGroup = (data: { id?: number; groupId?: number }): void => {
    this.render.registerGroup(data);
  };
  getGroup = (elementId: number): Set<number> | null =>
    this.render.getGroup(elementId);
  findPrimitive = (elementId: number): Primitive | null =>
    this.render.findPrimitive(elementId);
  add = (anno: Annotation): unknown => this.render.add(anno);
  remove = (anno: Annotation): boolean => this.render.remove(anno);
  redraw = (to: DrawTo) => this.render.redraw(to);

  // Select and start moving a graphics primitive, handling group membership.
  // If the primitive belongs to a group, selects all group members and enters
  // MoveGraphicsGroup mode. Otherwise enters MoveGraphics for the single element.
  startMove = (prim: Primitive): unknown => {
    const { editor, content } = this;
    const id = prim.data.id;
    const group = id !== undefined ? this.getGroup(id) : null;

    if (group && group.size > 1) {
      content.deselect();
      const targets: Array<Primitive> = [];
      for (const memberId of group) {
        const member = this.findPrimitive(memberId);
        if (!member) continue;
        content.addToSelection(new Annotation(member));
        targets.push(member);
      }
      return modes.MoveGraphicsGroup.enter(editor, targets);
    }

    content.select(new Annotation(prim));
    return modes.MoveGraphics.enter(editor, prim);
  };

  commit = (child: Primitive): unknown => {
    // Ensure the element has an ID before entering the change pipeline.
    if (child.data.id === undefined) {
      child.data.id = randomId();
    }
    const change = changes.add(new Annotation(child));
    this.content.logChange(change);
    this.editor.overlays.clearStatusMessage(DRAW_STATUS_KEY);
    return this.editor.goUiIdle();
  };

  // Add multiple graphics elements at once (e.g. from the catalogue).
  // Grouped as a single combo change so undo reverts them all.
  // Elements with a shared groupId are registered for joint selection/movement.
  addElements = (elements: Array<graphics.Element>): unknown => {
    const singleChanges: Array<changes.Single<changes.ChangeEntity>> = [];

    for (const elem of elements) {
      // GraphicsEntity.add() assigns IDs and creates the entity
      const prim = this.graphicsEntity.add(elem) as Primitive;
      singleChanges.push(changes.add(new Annotation(prim)));
      this.registerGroup(prim.data);
    }

    const result = changes.Combo.fromArray(singleChanges);
    if (result.ok) this.content.logChange(result.val as changes.Change<never>);
    this.editor.requestRedraw();
    return this.editor.goUiIdle();
  };

  startAddRect = () => AddRect.enter(this);
  startAddPolygon = () => AddPolygon.enter(this);
  startAddPath = () => AddPath.enter(this);
  startAddEllipse = () => AddEllipse.enter(this);
  startAddArc = () => AddArc.enter(this);

  // Enter "place text" mode: the next click chooses the (grid-snapped) location
  // and opens the Add Text dialog there. Used for keyboard/command-palette-driven
  // creation, where no click position is available up front.
  startAddText = () => AddText.enter(this);

  // Create a text annotation at `loc` carrying `text`, logging it as a single
  // add change. Unlike the shape draw modes, text has no mouse-drawn geometry —
  // its location comes from the (grid-snapped) click position and its string is
  // supplied by the edit dialog, so creation is a direct, programmatic add.
  addTextAt = (loc: model.Point, text: string): unknown => {
    const element = graphics.Text.new({
      ...graphics.Text.Format.default(),
      height: ANNOTATION_TEXT_HEIGHT,
      loc: schdata.Point.clone(loc),
      text,
    });
    return this.addElements([element]);
  };

  // Begin placing a text annotation carrying `text`: create a provisional text
  // entity (rendered, not yet logged) and enter PlaceText. Commit logs the add;
  // cancel removes it, logging nothing — a true no-op, like the shape modes.
  startPlaceText = (loc: model.Point, text: string): unknown => {
    const element = graphics.Text.new({
      ...graphics.Text.Format.default(),
      height: ANNOTATION_TEXT_HEIGHT,
      loc: schdata.Point.clone(loc),
      text,
    });
    // addText() (unlike add()) doesn't assign an id; the element needs one.
    element.id ??= randomId();
    const entity = this.graphicsEntity.addText(element);
    return PlaceText.enter(this, entity);
  };

  // Update the string of the text annotation with the given element id, logging
  // the edit for undo/redo. An empty string removes the annotation entirely,
  // matching the "empty removes" convention used for wire labels.
  updateText = (id: number, newText: string): unknown => {
    const { editor } = this;
    if (editor.isLocked) return null;

    const prim = this.findPrimitive(id);
    if (!prim || prim.entityKind !== EntityKind.Text) return null;

    const oldText = prim.data.text;
    if (newText === oldText) return null;

    // Empty text means remove the annotation.
    if (newText === "") {
      const anno = new Annotation(prim);
      this.remove(anno);
      this.content.logChange(changes.remove(anno));
      return editor.requestRedraw();
    }

    // Apply the text change, then log it for undo/redo.
    prim.update({ text: newText });
    this.content.logChange(
      changes.update({
        target: prim,
        from: { text: oldText },
        to: { text: newText },
      })
    );
    return editor.requestRedraw();
  };
}

abstract class AnnotationModeHandler extends modes.ModeHandler {
  readonly editor: InteractionHost;
  readonly graphicsEntity: GraphicsEntity;
  constructor(
    readonly annotations: Annotations //
  ) {
    super();
    // Grab references to these at construction time. `editor` comes from the
    // interaction facade (annotations), not `root.editor` (the render tier,
    // which is only a RenderHost).
    this.graphicsEntity = annotations.graphicsEntity;
    this.editor = annotations.editor;
  }
}

const DRAW_STATUS_KEY = "draw-graphics";

// Default height (font size) for newly created text annotations, in schematic
// units. Matches the default wire-label height for visual consistency.
const ANNOTATION_TEXT_HEIGHT = 16;

class AddRect extends AnnotationModeHandler {
  // Static method to enter this mode
  static enter(annotations: Annotations) {
    annotations.editor.overlays.setStatusMessage(
      "Drawing Graphics: Rectangle",
      DRAW_STATUS_KEY
    );
    return annotations.editor.setMode(new AddRect(annotations));
  }

  // On mouse up, place one corner of the rect, and move into the next mode.
  override handleMouseUp = () => {
    const { annotations, editor, graphicsEntity } = this;

    // Get the nearest on-grid point
    const point = editor.gridPointNearestToMouse();
    const rect = graphics.Rectangle.new({
      bbox: schdata.Bbox.fromTwoPoints([
        schdata.Point.clone(point),
        schdata.Point.clone(point),
      ]),
      filled: false,
    });

    // Add the rect to the graphics drawer
    const rectEntity = graphicsEntity.addRect(rect);
    // rectEntity.redraw(); // FIXME: signal we need to redraw

    // And move our editor to the other-corner state
    return editor.setMode(new AddRectPart2(annotations, rectEntity, point));
  };

  // No harm, no foul on abort; we haven't added anything temporary to the screen or data model.
  override abort = () => {
    this.editor.overlays.clearStatusMessage(DRAW_STATUS_KEY);
    return this.editor.goUiIdle();
  };
}

// State in which we've placed the first corner of a rectangle,
// and moving the mouse around moves the opposite corner.
class AddRectPart2 extends AnnotationModeHandler {
  constructor(
    annotations: Annotations,
    readonly rectEntity: RectEntity,
    readonly fixedPoint: schdata.Point
  ) {
    super(annotations);
  }

  // Update the location of our non-fixed corner to the current mouse location.
  private updateLoc = (): RedrawRequest => {
    const { editor, rectEntity, fixedPoint } = this;
    const point = editor.gridPointNearestToMouse();
    return rectEntity.update({
      bbox: schdata.Bbox.fromTwoPoints([fixedPoint, point]),
    });
  };

  override handleMouseMove = () => this.updateLoc();

  // On mouse up, make our final location update and commit.
  override handleMouseUp = () => {
    this.updateLoc();
    return this.annotations.commit(this.rectEntity);
  };

  // Remove from parent on abort
  override abort = () => {
    this.graphicsEntity.removeRect(this.rectEntity);
    this.editor.overlays.clearStatusMessage(DRAW_STATUS_KEY);
    return this.editor.goUiIdle();
  };
}

// Place a text annotation with a single click. Text has no drawn geometry, so
// this mode just captures the click location and hands off to the IDE dialog
// (via the platform) to collect the string; the actual add happens once the
// user confirms the dialog.
class AddText extends AnnotationModeHandler {
  static enter(annotations: Annotations) {
    annotations.editor.overlays.setStatusMessage(
      "Add Text: click to place",
      DRAW_STATUS_KEY
    );
    return annotations.editor.setMode(new AddText(annotations));
  }

  override handleMouseUp = () => {
    const { editor } = this;
    const point = editor.gridPointNearestToMouse();
    editor.overlays.clearStatusMessage(DRAW_STATUS_KEY);
    editor.platform.notifyAddTextAnnotation({ x: point.x, y: point.y });
    return editor.goUiIdle();
  };

  override abort = () => {
    this.editor.overlays.clearStatusMessage(DRAW_STATUS_KEY);
    return this.editor.goUiIdle();
  };
}

// Cursor-attached placement of a text annotation whose string came from the
// dialog (see Annotations.startPlaceText). Follows the cursor; commits (logs
// the add) on click/Enter, or removes the provisional on right-click/Escape.
class PlaceText extends AnnotationModeHandler {
  constructor(annotations: Annotations, readonly entity: TextEntity) {
    super(annotations);
    annotations.editor.overlays.setStatusMessage(
      "Place Text: left-click to confirm, right-click to cancel",
      DRAW_STATUS_KEY
    );
  }

  static enter(annotations: Annotations, entity: TextEntity): unknown {
    const mode = new PlaceText(annotations, entity);
    const result = annotations.editor.setMode(mode);
    mode.updateLoc(); // Snap onto the cursor immediately.
    return result;
  }

  private updateLoc = (): RedrawRequest => {
    const point = this.editor.gridPointNearestToMouse();
    return this.entity.update({ loc: schdata.Point.clone(point) });
  };

  override handleMouseMove = () => this.updateLoc();

  // Left-click confirms; a non-left button (right-click) cancels.
  override handleMouseDown = (e: MouseEvent) => {
    if (e.button !== 0) return this.abort();
  };

  override handleMouseUp = () => {
    this.updateLoc();
    return this.annotations.commit(this.entity);
  };

  override handleKey = (e: KeyboardEvent) => {
    // Widen to `string` for a plain string compare (a direct
    // `e.key === Keys.Enter` trips the enum-comparison lint rule).
    const enterKey: string = Keys.Enter;
    if (e.key === enterKey) return this.annotations.commit(this.entity);
  };

  // Escape (routed here) removes the provisional entity — nothing was logged.
  override abort = () => {
    this.graphicsEntity.removeTextEntity(this.entity);
    this.editor.overlays.clearStatusMessage(DRAW_STATUS_KEY);
    return this.editor.goUiIdle();
  };
}

class AddPolygon extends AnnotationModeHandler {
  // Static method to enter this mode
  static enter(annotations: Annotations) {
    annotations.editor.overlays.setStatusMessage(
      "Drawing Graphics: Polygon",
      DRAW_STATUS_KEY
    );
    return annotations.editor.setMode(new AddPolygon(annotations));
  }

  override handleMouseUp = () => {
    const { annotations, graphicsEntity, editor } = this;

    const point = editor.gridPointNearestToMouse();
    const polygon = graphics.Polygon.new({
      points: [schdata.Point.clone(point), point],
      filled: false,
    });

    const polygonEntity = graphicsEntity.addPolygon(polygon);
    // polygonEntity.redraw(); // FIXME: signal we need to redraw

    return editor.setMode(new AddPolygonVertex(annotations, polygonEntity));
  };

  override abort = () => {
    this.editor.overlays.clearStatusMessage(DRAW_STATUS_KEY);
    return this.editor.goUiIdle();
  };
}

// Base class for adding vertices to either paths or polygons
abstract class AddVertex extends AnnotationModeHandler {
  //
  abstract readonly entity: PolygonEntity | PathEntity;

  // Event handlers
  override handleMouseMove = () => this.updateLastVertex();
  override handleMouseUp = () => this.addVertex();
  override handleDoubleClick = () => this.commit();
  override handleKey = (e: KeyboardEvent) => {
    if (e.key === Keys.Enter) return this.commit();
  };

  // Move the last vertex to the current mouse location
  private updateLastVertex = (): RedrawRequest => {
    const { editor, entity } = this;
    const { points } = entity.data;
    const rest = points.slice(0, points.length - 1); // Grab all points but the last
    const point = editor.gridPointNearestToMouse();
    return entity.update({ points: [...rest, point] }); // Update the last one to the mouse location
  };

  // Add a vertex at the current mouse location
  private addVertex = (): RedrawRequest => {
    const { editor, entity } = this;
    const { points } = entity.data;
    const rest = points.slice(0, points.length - 1); // Grab all points but the last
    const point = editor.gridPointNearestToMouse();
    return entity.update({
      points: [...rest, schdata.Point.clone(point), point],
    });
  };

  private commit = () => {
    this.updateLastVertex();
    return this.annotations.commit(this.entity);
  };
}

class AddPolygonVertex extends AddVertex {
  constructor(
    annotations: Annotations,
    override readonly entity: PolygonEntity
  ) {
    super(annotations);
  }
  // Remove from parent on abort
  override abort = () => {
    this.graphicsEntity.removePolygon(this.entity);
    this.editor.overlays.clearStatusMessage(DRAW_STATUS_KEY);
    return this.editor.goUiIdle();
  };
}

class AddPath extends AnnotationModeHandler {
  // Static method to enter this mode
  static enter(annotations: Annotations) {
    annotations.editor.overlays.setStatusMessage(
      "Drawing Graphics: Path",
      DRAW_STATUS_KEY
    );
    return annotations.editor.setMode(new AddPath(annotations));
  }

  override handleMouseUp = () => {
    const { annotations, graphicsEntity } = this;
    const { editor } = annotations;

    const point = editor.gridPointNearestToMouse();
    const path = graphics.Path.new({
      points: [schdata.Point.clone(point), schdata.Point.clone(point)],
      style: graphics.PathStyle.default(),
    });

    const pathEntity = graphicsEntity.addPath(path);
    // pathEntity.redraw();// FIXME: signal we need to redraw

    return editor.setMode(new AddPathVertex(annotations, pathEntity));
  };

  override abort = () => {
    this.editor.overlays.clearStatusMessage(DRAW_STATUS_KEY);
    return this.editor.goUiIdle();
  };
}

class AddPathVertex extends AddVertex {
  constructor(annotations: Annotations, override readonly entity: PathEntity) {
    super(annotations);
  }
  // Remove from parent on abort
  override abort = () => {
    this.graphicsEntity.removePath(this.entity);
    this.editor.overlays.clearStatusMessage(DRAW_STATUS_KEY);
    return this.editor.goUiIdle();
  };
}

// # Ellipse drawing (two clicks)
//
// Click 1 places the center; click 2 places one bbox corner. As the mouse
// moves between the two clicks, a provisional ellipse — centered on the first
// click, with the cursor as the nearest bbox corner — is drawn live.
class AddEllipse extends AnnotationModeHandler {
  static enter(annotations: Annotations) {
    annotations.editor.overlays.setStatusMessage(
      "Drawing Graphics: Ellipse",
      DRAW_STATUS_KEY
    );
    return annotations.editor.setMode(new AddEllipse(annotations));
  }

  // First click: place the center, create a (zero-size) provisional ellipse,
  // and move into the corner-dragging state.
  override handleMouseUp = () => {
    const { annotations, editor, graphicsEntity } = this;
    const center = editor.gridPointNearestToMouse();
    const ellipse = graphics.Ellipse.new({
      bbox: schdata.Bbox.fromTwoPoints([
        schdata.Point.clone(center),
        schdata.Point.clone(center),
      ]),
      filled: false,
    });
    const ellipseEntity = graphicsEntity.addEllipse(ellipse);
    return editor.setMode(
      new AddEllipsePart2(annotations, ellipseEntity, center)
    );
  };

  // Nothing temporary on screen yet, so abort is a clean no-op.
  override abort = () => {
    this.editor.overlays.clearStatusMessage(DRAW_STATUS_KEY);
    return this.editor.goUiIdle();
  };
}

// State after the ellipse center is placed: the cursor drives one bbox corner,
// with the center held fixed (the opposite corner mirrors across it).
class AddEllipsePart2 extends AnnotationModeHandler {
  constructor(
    annotations: Annotations,
    readonly ellipseEntity: EllipseEntity,
    readonly center: schdata.Point
  ) {
    super(annotations);
  }

  // Reshape the bbox so it stays centered on `center` with the cursor at a corner.
  private updateBbox = (): RedrawRequest => {
    const { editor, ellipseEntity, center } = this;
    const corner = editor.gridPointNearestToMouse();
    const opposite = mirrorAcross(center, corner);
    return ellipseEntity.update({
      bbox: schdata.Bbox.fromTwoPoints([corner, opposite]),
    });
  };

  override handleMouseMove = () => this.updateBbox();

  override handleMouseUp = () => {
    this.updateBbox();
    return this.annotations.commit(this.ellipseEntity);
  };

  override abort = () => {
    this.graphicsEntity.removeEllipse(this.ellipseEntity);
    this.editor.overlays.clearStatusMessage(DRAW_STATUS_KEY);
    return this.editor.goUiIdle();
  };
}

// # Arc drawing (three clicks)
//
// Click 1 places the center. Click 2 fixes the ellipse (cursor = nearest bbox
// corner) and the start angle (the direction from center to cursor). Click 3
// fixes the end angle on that same, now-frozen ellipse. A provisional ellipse /
// arc is drawn live between clicks. Start and stop angles are fixed once set.
class AddArc extends AnnotationModeHandler {
  static enter(annotations: Annotations) {
    annotations.editor.overlays.setStatusMessage(
      "Drawing Graphics: Arc",
      DRAW_STATUS_KEY
    );
    return annotations.editor.setMode(new AddArc(annotations));
  }

  // First click: place the center and create a provisional full-ellipse arc.
  override handleMouseUp = () => {
    const { annotations, editor, graphicsEntity } = this;
    const center = editor.gridPointNearestToMouse();
    const bbox = schdata.Bbox.fromTwoPoints([
      schdata.Point.clone(center),
      schdata.Point.clone(center),
    ]);
    const arc = graphics.Arc.new({
      ellipseBbox: bbox,
      arcBbox: schdata.Bbox.clone(bbox),
      startAngle: 0,
      stopAngle: FULL_CIRCLE,
    });
    const arcEntity = graphicsEntity.addArc(arc);
    return editor.setMode(
      new AddArcDefineEllipse(annotations, arcEntity, center)
    );
  };

  override abort = () => {
    this.editor.overlays.clearStatusMessage(DRAW_STATUS_KEY);
    return this.editor.goUiIdle();
  };
}

// Second arc state: the cursor drives the ellipse size/shape (center fixed),
// shown as a live full ellipse. Clicking fixes the ellipse and the start angle.
class AddArcDefineEllipse extends AnnotationModeHandler {
  constructor(
    annotations: Annotations,
    readonly arcEntity: ArcEntity,
    readonly center: schdata.Point
  ) {
    super(annotations);
  }

  // The ellipse bbox for the current cursor position (cursor = nearest corner).
  private bboxForMouse = (): schdata.Bbox => {
    const corner = this.editor.gridPointNearestToMouse();
    return schdata.Bbox.fromTwoPoints([
      corner,
      mirrorAcross(this.center, corner),
    ]);
  };

  // Live preview: a full ellipse of the current size.
  private updateEllipse = (): RedrawRequest => {
    const bbox = this.bboxForMouse();
    return this.arcEntity.update({
      ellipseBbox: bbox,
      arcBbox: schdata.Bbox.clone(bbox),
      startAngle: 0,
      stopAngle: FULL_CIRCLE,
    });
  };

  override handleMouseMove = () => this.updateEllipse();

  // Click: freeze the ellipse and the start angle (center → cursor direction).
  override handleMouseUp = () => {
    const { arcEntity, center, editor } = this;
    const corner = editor.gridPointNearestToMouse();
    const ellipseBbox = schdata.Bbox.fromTwoPoints([
      corner,
      mirrorAcross(center, corner),
    ]);
    const startAngle = graphics.Arc.normAngle(
      Math.atan2(corner.y - center.y, corner.x - center.x)
    );
    // Start as a zero-length arc; click 3 grows it to the end angle.
    arcEntity.update({
      ellipseBbox,
      arcBbox: schdata.Bbox.clone(ellipseBbox),
      startAngle,
      stopAngle: startAngle,
    });
    return editor.setMode(
      new AddArcDefineEnd(this.annotations, arcEntity, center)
    );
  };

  override abort = () => {
    this.graphicsEntity.removeArcEntity(this.arcEntity);
    this.editor.overlays.clearStatusMessage(DRAW_STATUS_KEY);
    return this.editor.goUiIdle();
  };
}

// Third arc state: the ellipse and start angle are frozen; the cursor drives the
// end angle along the same ellipse. Clicking commits.
class AddArcDefineEnd extends AnnotationModeHandler {
  constructor(
    annotations: Annotations,
    readonly arcEntity: ArcEntity,
    readonly center: schdata.Point
  ) {
    super(annotations);
  }

  // Sweep the stop angle to the cursor's direction. The ellipse is frozen, so
  // arcBbox (which just tracks the ellipse bbox) doesn't change here.
  private updateEnd = (): RedrawRequest => {
    const { editor, arcEntity, center } = this;
    const m = editor.gridPointNearestToMouse();
    const stopAngle = graphics.Arc.normAngle(
      Math.atan2(m.y - center.y, m.x - center.x)
    );
    return arcEntity.update({ stopAngle });
  };

  override handleMouseMove = () => this.updateEnd();

  override handleMouseUp = () => {
    this.updateEnd();
    return this.annotations.commit(this.arcEntity);
  };

  override abort = () => {
    this.graphicsEntity.removeArcEntity(this.arcEntity);
    this.editor.overlays.clearStatusMessage(DRAW_STATUS_KEY);
    return this.editor.goUiIdle();
  };
}

// A full revolution in radians; used for the live full-ellipse arc preview.
const FULL_CIRCLE = 2 * Math.PI;

// Reflect `point` through `center` (the diagonally-opposite bbox corner).
function mirrorAcross(center: model.Point, point: model.Point): model.Point {
  return { x: 2 * center.x - point.x, y: 2 * center.y - point.y };
}
